import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AccountBalance as TaxIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getTaxes, createTax, updateTax, deleteTax } from '../redux/actions/taxActions';

const TaxMaster = () => {
  const dispatch = useDispatch();
  const { taxes, taxesLoading, taxesError, taxesPagination, loading, error, success } = useSelector(state => state.taxes);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [formData, setFormData] = useState({
    hsn_code: '',
    description: '',
    gst_percent: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchTerm]);

  const fetchData = () => {
    dispatch(getTaxes({
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm
    }));
  };

  const handleOpenDialog = (tax = null) => {
    setEditingTax(tax);
    setFormData(tax ? {
      hsn_code: tax.hsn_code,
      description: tax.description,
      gst_percent: tax.gst_percent
    } : {
      hsn_code: '',
      description: '',
      gst_percent: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTax(null);
    setFormData({
      hsn_code: '',
      description: '',
      gst_percent: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTax) {
        await dispatch(updateTax(editingTax.id, formData));
      } else {
        await dispatch(createTax(formData));
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving tax:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tax?')) {
      try {
        await dispatch(deleteTax(id));
        fetchData();
      } catch (error) {
        console.error('Error deleting tax:', error);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Tax Master
      </Typography>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch({ type: 'CLEAR_SUCCESS' })}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch({ type: 'CLEAR_ERROR' })}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Side - Tax List */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Tax List</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Tax
                </Button>
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search by HSN Code or Description..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Tax Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>HSN Code</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>GST %</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taxesLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : taxes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No taxes found
                        </TableCell>
                      </TableRow>
                    ) : (
                      taxes.map((tax) => (
                        <TableRow key={tax.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {tax.hsn_code}
                            </Typography>
                          </TableCell>
                          <TableCell>{tax.description}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${tax.gst_percent}%`}
                              color={tax.gst_percent <= 5 ? 'success' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenDialog(tax)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(tax.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {taxesPagination && (
                <TablePagination
                  component="div"
                  count={taxesPagination.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Add/Edit Form */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TaxIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {editingTax ? 'Edit Tax' : 'Add New Tax'}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {editingTax 
                  ? 'Update the tax information below.' 
                  : 'Fill in the details to add a new tax entry.'
                }
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="HSN Code"
                  name="hsn_code"
                  value={formData.hsn_code}
                  onChange={handleInputChange}
                  required
                  sx={{ mb: 2 }}
                  placeholder="e.g., 7113"
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  sx={{ mb: 2 }}
                  placeholder="e.g., Gold Jewellery"
                />

                <TextField
                  fullWidth
                  label="GST Percentage"
                  name="gst_percent"
                  type="number"
                  value={formData.gst_percent}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ mb: 3 }}
                  placeholder="e.g., 3.00"
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {loading ? 'Saving...' : editingTax ? 'Update Tax' : 'Add Tax'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTax ? 'Edit Tax' : 'Add New Tax'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="HSN Code"
                  name="hsn_code"
                  value={formData.hsn_code}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GST Percentage"
                  name="gst_percent"
                  type="number"
                  value={formData.gst_percent}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : editingTax ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default TaxMaster;
